import dayjs from "dayjs";

type LocaleDateFormat = {
  full: string;
  monthDay: string;
};

const fallbackFormat: LocaleDateFormat = {
  full: "YYYY-MM-DD",
  monthDay: "MM-DD",
};

function normalizeLocaleCode(input: string): string {
  const sanitized = input.replace("_", "-").trim();
  const [languagePart, regionPart] = sanitized.split("-");
  if (!languagePart || !regionPart) return sanitized;
  return `${languagePart.toLowerCase()}-${regionPart.toUpperCase()}`;
}

export function resolveLocale(locale?: string): string {
  if (locale && locale.trim()) return normalizeLocaleCode(locale);

  const profileLanguage =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("hej-language") || ""
      : "";

  if (profileLanguage) {
    return normalizeLocaleCode(profileLanguage);
  }

  const globalWindow =
    typeof window !== "undefined"
      ? (window as Window & { BLABLA_LANGUAGE?: string })
      : undefined;

  const appLanguage =
    typeof globalWindow?.BLABLA_LANGUAGE === "string"
      ? globalWindow.BLABLA_LANGUAGE
      : "";

  if (appLanguage) {
    return normalizeLocaleCode(appLanguage);
  }

  return normalizeLocaleCode(
    Intl.DateTimeFormat().resolvedOptions().locale || "en-GB"
  );
}

export function getDateFormatsForLocale(locale: string): LocaleDateFormat {
  switch (locale) {
    case "sv-SE":
    case "en-SE":
    case "lt-LT":
    case "zh-CN":
    case "en-CN":
    case "ja-JP":
    case "en-JP":
    case "ko-KR":
    case "en-KR":
    case "en-MY":
    case "ms-MY":
    case "uk-UA":
      return { full: "YYYY-MM-DD", monthDay: "MM-DD" };

    case "en-US":
    case "en-CA":
      return { full: "MM/DD/YYYY", monthDay: "MM/DD" };

    case "de-DE":
    case "de-CH":
    case "fr-CH":
    case "it-CH":
    case "de-AT":
    case "cs-CZ":
    case "fi-FI":
    case "pl-PL":
    case "sk-SK":
    case "sl-SI":
    case "sr-RS":
    case "ru-RU":
    case "hr-HR":
    case "no-NO":
    case "ro-RO":
      return { full: "DD.MM.YYYY", monthDay: "DD.MM" };

    case "hu-HU":
      return { full: "YYYY.MM.DD", monthDay: "MM.DD" };

    case "en-GB":
    case "en-IE":
    case "en-AU":
    case "en-IN":
    case "fr-FR":
    case "es-ES":
    case "en-ES":
    case "ca-ES":
    case "eu-ES":
    case "gl-ES":
    case "it-IT":
    case "pt-PT":
    case "en-PT":
    case "nl-BE":
    case "fr-BE":
    case "nl-NL":
    case "en-NL":
    case "da-DK":
    case "en-SG":
    case "ar-BH":
    case "en-BH":
    case "ar-JO":
    case "en-JO":
    case "ar-KW":
    case "en-KW":
    case "en-QA":
    case "en-EG":
    case "ar-MA":
    case "en-MA":
    case "fr-MA":
    case "ar-SA":
    case "en-SA":
    case "en-AE":
      return { full: "DD/MM/YYYY", monthDay: "DD/MM" };

    default:
      return fallbackFormat;
  }
}

export function formatDateByLocale(
  dateInput: string | Date,
  locale?: string
): string {
  const date = dayjs(dateInput);
  if (!date.isValid()) return "";
  return date.format(getDateFormatsForLocale(resolveLocale(locale)).full);
}

export function formatDateTimeByLocale(
  dateInput: string | Date,
  locale?: string
): string {
  const date = dayjs(dateInput);
  if (!date.isValid()) return "";
  return `${date.format(getDateFormatsForLocale(resolveLocale(locale)).full)} ${date.format("HH:mm")}`;
}
